import { FieldArray, Form, Formik } from "formik";
import * as yup from "yup";
import { FormikInput } from "@/components/common/form/FormikInput.tsx";
import { Button } from "@/components/base/Button.tsx";
import { useState } from "react";
import { PatchTenantDto, Tenant } from "@/models";
import { createTenantJoinCode } from "@/repositories/tenant.ts";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/base/Popover.tsx";
import { CopyIcon } from "lucide-react";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard.ts";
import { DEFAULT_SERVICE_OPTIONS } from "@/lib/const.ts";

type EditCompanySettingsFormProps = {
  onSubmit: (values: PatchTenantDto) => Promise<void>;
  initialData: Tenant;
  onCancel?: () => void;
  isEditable?: boolean;
};

const validationSchema = yup.object({
  joinCode: yup
    .string()
    .required("Join code is required")
    .matches(/^\w{4}-\w{4}$/, "Join code must be in the format XXXX-XXXX")
    .length(9, "Join code must be in the format XXXX-XXXX"),
  name: yup.string().required("Company name is required"),
  serviceOptions: yup.array().of(
    yup.object().shape({
      value: yup.string().required("Value is required"),
      label: yup.string().required("Label is required"),
    })
  ),
});

export const EditCompanySettingsForm = ({
  initialData,
  onCancel,
  onSubmit,
  isEditable,
}: EditCompanySettingsFormProps) => {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const [copiedText, copy] = useCopyToClipboard();
  const [copyPopoverOpen, setCopyPopoverOpen] = useState(false);
  const [copyResults, setCopyResults] = useState({ success: false, error: "" });

  const handleCopy = (text: string) => {
    copy(text)
      .then(() => {
        setCopyResults({ success: true, error: "" });
      })
      .catch((error) => {
        setCopyResults({ success: false, error: error });
        console.error(error);
      })
      .finally(() => {
        setTimeout(() => {
          setCopyPopoverOpen(false);
        }, 2000);
      });
  };

  const sanitizeValue = (label: string): string => {
    return label
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "");
  };

  return (
    <Formik
      initialValues={{
        joinCode: initialData.joinCode ?? "",
        name: initialData.name ?? "",
        serviceOptions:
          initialData.settings?.serviceOptions ?? DEFAULT_SERVICE_OPTIONS,
      }}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        const submitData: PatchTenantDto = {
          id: initialData.id,
          name: values.name,
          joinCode: values.joinCode,
          settings: {
            ...initialData.settings,
            serviceOptions: values.serviceOptions,
          },
        } as PatchTenantDto;

        onSubmit(submitData).finally(() => setSubmitting(false));
        return;
      }}
    >
      {({ values, setFieldValue, handleSubmit }) => (
        <Form
          className="flex flex-col sm:grid sm:grid-cols-2 gap-4 m-auto w-64 sm:w-full max-w-screen-md"
          onSubmit={handleSubmit}
        >
          <div className="flex col-span-2 sm:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Basic Information</h3>
            <FormikInput
              name="name"
              type="text"
              id="name"
              label="Company Name"
              disabled={!isEditable}
            />
            <div className="flex flex-row justify-between items-end">
              <FormikInput
                className="flex-grow"
                name="joinCode"
                type="text"
                id="joinCode"
                label="Join Code"
                disabled={!isEditable}
              />
              {isEditable && (
                <Button
                  type="button"
                  disabled={!isEditable}
                  onClick={() =>
                    setFieldValue("joinCode", createTenantJoinCode())
                  }
                >
                  Generate Code
                </Button>
              )}
              <Popover open={copyPopoverOpen} onOpenChange={setCopyPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    disabled={isEditable}
                    variant="ghost"
                    size="icon"
                    onClick={() => handleCopy(values?.joinCode)}
                  >
                    <CopyIcon />
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  {copyResults.success
                    ? `${copiedText} copied to clipboard!`
                    : `Error copying: ${copyResults.error}`}
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex col-span-2 sm:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Service Types</h3>
            <FieldArray name="serviceOptions">
              {({ remove, push }) => (
                <div>
                  {values.serviceOptions.length > 0 &&
                    values.serviceOptions.map((option, index) => (
                      <div
                        className="flex flex-row justify-between items-end gap-2"
                        key={index}
                      >
                        <FormikInput
                          disabled={!isEditable}
                          className="flex-grow"
                          name={`options.${index}.label`}
                          type="text"
                          placeholder="Label"
                          label={`Option #${index + 1} Label`}
                          value={option?.label}
                          onChange={(e) => {
                            const label = e.target.value;
                            const value = sanitizeValue(label);
                            setFieldValue(
                              `serviceOptions.${index}.label`,
                              label
                            );
                            setFieldValue(
                              `serviceOptions.${index}.value`,
                              value
                            );
                          }}
                        />
                        {isEditable && (
                          <Button
                            disabled={!isEditable}
                            variant="secondary"
                            type="button"
                            onClick={() => remove(index)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ))}
                  {isEditable && (
                    <Button
                      disabled={!isEditable}
                      className="mt-2"
                      variant="default"
                      type="button"
                      onClick={() => push({ label: "", value: "" })}
                    >
                      Add Option
                    </Button>
                  )}
                </div>
              )}
            </FieldArray>
          </div>

          {isEditable && (
            <div className="flex md:col-span-3 flex-row gap-4 justify-end">
              <Button
                variant={"secondary"}
                onClick={handleCancel}
                type="button"
              >
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};
